import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@gelirgider/ui';
import { loginSchema, type LoginInput } from '@gelirgider/validators';
import { api, ApiException } from '../lib/api/client';
import { useAuthStore, type AdminUser, type AuthState } from '../lib/stores/auth-store';

interface LoginResponse {
  accessToken: string;
  user: AdminUser;
}

export function LoginPage(): React.ReactElement {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const setSession = useAuthStore((s: AuthState) => s.setSession);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: LoginInput): Promise<void> {
    setSubmitting(true);
    try {
      const data = await api<LoginResponse>('/auth/login', { method: 'POST', body: values });
      if (!data.user.isAdmin) {
        toast.error(t('login.notAdmin'));
        setSubmitting(false);
        return;
      }
      setSession(data.accessToken, data.user);
      void navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiException) {
        toast.error(err.statusCode === 401 ? t('login.invalid') : err.message);
      } else {
        toast.error(t('error.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md space-y-4">
        <div className="flex items-center justify-center gap-2">
          <Shield className="h-6 w-6 text-primary" aria-hidden="true" />
          <span className="text-xl font-semibold">{t('topbar.title')}</span>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>{t('login.title')}</CardTitle>
            <CardDescription>{t('login.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
              aria-busy={submitting}
            >
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  inputMode="email"
                  required
                  aria-invalid={form.formState.errors.email ? true : undefined}
                  aria-describedby={form.formState.errors.email ? 'email-error' : undefined}
                  {...form.register('email')}
                  disabled={submitting}
                />
                {form.formState.errors.email ? (
                  <p id="email-error" role="alert" className="text-xs text-destructive">
                    {form.formState.errors.email.message}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('login.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  aria-invalid={form.formState.errors.password ? true : undefined}
                  aria-describedby={
                    form.formState.errors.password ? 'password-error' : undefined
                  }
                  {...form.register('password')}
                  disabled={submitting}
                />
                {form.formState.errors.password ? (
                  <p id="password-error" role="alert" className="text-xs text-destructive">
                    {form.formState.errors.password.message}
                  </p>
                ) : null}
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? t('login.signingIn') : t('login.submit')}
              </Button>
              <div className="text-center text-xs text-muted-foreground">
                <a
                  href="/forgot-password"
                  className="hover:text-foreground hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    toast.info(t('login.forgotPassword'));
                  }}
                >
                  {t('login.forgotPassword')}
                </a>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
